/**
 * Reference utilities for the References Section.
 *
 * The enrichment itself (`enrichCitations`) lives behind `@heidi/evidence/contracts`;
 * this module binds it to Chat's `TextResult` chunks.
 *
 * The collectCitationsFromChunks function is still needed for deduplication.
 */

import type {
  EnrichedReference,
  SimpleCitation,
  Source,
  TextResult,
} from '../../contracts';
import { enrichCitations, getCitationKey } from '../../contracts';

/**
 * Collect and deduplicate citations from text chunks.
 * Returns unique citations by appropriate key based on source type.
 */
export function collectCitationsFromChunks(chunks: TextResult[]): SimpleCitation[] {
  const seen = new Set<string>();
  const citations: SimpleCitation[] = [];

  for (const chunk of chunks) {
    if (!chunk.citations?.length) continue;

    for (const citation of chunk.citations) {
      const key = getCitationKey(citation);

      if (!seen.has(key)) {
        seen.add(key);
        citations.push(citation);
      }
    }
  }

  return citations;
}

/**
 * Convenience function to collect and enrich citations in one step.
 *
 * @param chunks - Text chunks with citations
 * @param webSearchSources - Sources from web_search_call chunks (have page_age)
 */
export function getEnrichedReferences(
  chunks: TextResult[],
  webSearchSources: Source[] = []
): EnrichedReference[] {
  const citations = collectCitationsFromChunks(chunks);
  return enrichCitations(citations, webSearchSources);
}
